<script setup lang="ts">
	/**
	 * Editor de las marcas de la empresa en el correo: plantilla + dónde va cada imagen.
	 *
	 * ⚠️ Se arrastra a RANURAS, no a coordenadas libres. Un correo no es una página web:
	 * Outlook lo renderiza con el motor de Word, que no soporta posicionamiento absoluto ni
	 * flexbox. Un editor de píxeles se vería bien aquí y llegaría descuadrado a media base de
	 * clientes. Las ranuras son celdas que ya existen en la plantilla del correo, así que
	 * soltar hace snap a una de ellas y lo que viaja es HTML de tabla, que sí sobrevive.
	 *
	 * Se usa HTML5 drag-and-drop nativo y no una librería: son cinco zonas y dos piezas, y
	 * meter una dependencia para esto es más peso del que ahorra.
	 */
	import { computed, ref } from 'vue'

	import {
		EMAIL_SLOTS,
		EMAIL_TEMPLATES,
		EMAIL_VARIANTS,
		type EmailLayout,
		type EmailSlot,
		type EmailTemplate,
		type EmailVariant,
	} from '@/modules/companies/interfaces/emailSettings'

	const props = defineProps<{
		modelValue: EmailLayout
		disabled?: boolean
		/** URLs de las imágenes de la empresa, para que el editor enseñe lo real. */
		logoUrl?: string | null
		isotipoUrl?: string | null
	}>()

	const emit = defineEmits<{ 'update:modelValue': [EmailLayout] }>()

	const dragging = ref<EmailVariant | null>(null)
	const hovered = ref<EmailSlot | null>(null)

	const headerSlots = computed(() =>
		EMAIL_SLOTS.filter((s) => s.zone === 'header')
	)

	function variantAt(slot: EmailSlot): EmailVariant | null {
		return props.modelValue.marks.find((m) => m.slot === slot)?.variant ?? null
	}

	function imageFor(variant: EmailVariant): string | null {
		return variant === 'logo' ?
				(props.logoUrl ?? null)
			:	(props.isotipoUrl ?? null)
	}

	/** Una empresa que no subió esa imagen no puede colocarla: se muestra deshabilitada. */
	function isAvailable(variant: EmailVariant): boolean {
		return !!imageFor(variant)
	}

	function emitLayout(next: Partial<EmailLayout>) {
		emit('update:modelValue', { ...props.modelValue, ...next })
	}

	function setTemplate(template: EmailTemplate) {
		if (props.disabled) return
		emitLayout({ template })
	}

	function onDragStart(variant: EmailVariant) {
		if (props.disabled || !isAvailable(variant)) return
		dragging.value = variant
	}

	function onDrop(slot: EmailSlot) {
		hovered.value = null
		const variant = dragging.value
		dragging.value = null
		if (props.disabled || !variant) return

		// Una ranura sostiene UNA marca y una marca vive en UNA ranura: se quita la anterior
		// de esa variante y lo que hubiera en la ranura destino. Sin esto, arrastrar el logo
		// dos veces lo dejaba duplicado en el correo.
		const marks = props.modelValue.marks
			.filter((m) => m.slot !== slot && m.variant !== variant)
			.concat({ variant, slot })

		emitLayout({ marks })
	}

	function removeAt(slot: EmailSlot) {
		if (props.disabled) return
		emitLayout({ marks: props.modelValue.marks.filter((m) => m.slot !== slot) })
	}

	const isDark = computed(() => props.modelValue.template !== 'minimal')
</script>

<template>
	<div>
		<!-- Plantillas -->
		<p class="text-xs font-semibold uppercase tracking-wide text-gray-400">
			Plantilla
		</p>
		<div class="mt-2 grid gap-2 sm:grid-cols-3">
			<button
				v-for="tpl in EMAIL_TEMPLATES"
				:key="tpl.value"
				type="button"
				:disabled="disabled"
				:class="[
					'rounded-xl border px-3 py-2.5 text-left transition',
					modelValue.template === tpl.value ?
						'border-[#253C70] bg-[#253C70]/5 ring-1 ring-[#253C70]'
					:	'border-gray-200 hover:border-gray-300',
					disabled ? 'cursor-not-allowed opacity-60' : '',
				]"
				@click="setTemplate(tpl.value)"
			>
				<span class="block text-sm font-semibold text-gray-900">{{
					tpl.label
				}}</span>
				<span class="mt-0.5 block text-xs leading-snug text-gray-400">{{
					tpl.description
				}}</span>
			</button>
		</div>

		<!-- Piezas que se arrastran -->
		<p class="mt-5 text-xs font-semibold uppercase tracking-wide text-gray-400">
			Arrastra tu marca a una posición
		</p>
		<div class="mt-2 flex flex-wrap gap-2">
			<div
				v-for="v in EMAIL_VARIANTS"
				:key="v.value"
				:draggable="!disabled && isAvailable(v.value)"
				:class="[
					'flex items-center gap-2 rounded-lg border px-3 py-2',
					isAvailable(v.value) ?
						'cursor-grab border-gray-200 bg-white active:cursor-grabbing'
					:	'cursor-not-allowed border-dashed border-gray-200 bg-gray-50',
				]"
				@dragstart="onDragStart(v.value)"
				@dragend="dragging = null"
			>
				<img
					v-if="imageFor(v.value)"
					:src="imageFor(v.value)!"
					alt=""
					class="h-5 max-w-16 object-contain"
				/>
				<span
					:class="[
						'text-xs font-medium',
						isAvailable(v.value) ? 'text-gray-700' : 'text-gray-400',
					]"
					>{{ v.label }}</span
				>
				<span
					v-if="!isAvailable(v.value)"
					class="text-[10px] text-gray-400"
					>sin cargar</span
				>
			</div>
		</div>

		<!-- Maqueta del correo con sus ranuras -->
		<div class="mt-4 overflow-hidden rounded-xl border border-gray-200">
			<!-- Encabezado -->
			<div
				:class="[
					'px-3 py-3',
					isDark ? 'bg-gradient-to-br from-[#0a1f38] to-[#0f3460]' : 'bg-white',
				]"
			>
				<div class="grid grid-cols-3 gap-2">
					<div
						v-for="slot in headerSlots"
						:key="slot.value"
						:class="[
							'flex h-14 items-center justify-center rounded-lg border-2 border-dashed transition',
							hovered === slot.value ? 'border-emerald-400 bg-emerald-400/10'
							: isDark ? 'border-white/25'
							: 'border-gray-200',
						]"
						@dragover.prevent="hovered = slot.value"
						@dragleave="hovered = null"
						@drop.prevent="onDrop(slot.value)"
					>
						<div
							v-if="variantAt(slot.value)"
							class="group relative flex items-center"
						>
							<span class="rounded bg-white px-1.5 py-1">
								<img
									:src="imageFor(variantAt(slot.value)!)!"
									alt=""
									class="h-5 max-w-16 object-contain"
								/>
							</span>
							<button
								type="button"
								:disabled="disabled"
								class="ml-1 text-[10px] text-white/70 hover:text-white"
								:class="{ 'text-gray-400 hover:text-gray-600': !isDark }"
								title="Quitar"
								@click="removeAt(slot.value)"
							>
								✕
							</button>
						</div>
						<span
							v-else
							:class="[
								'text-[10px]',
								isDark ? 'text-white/40' : 'text-gray-300',
							]"
							>{{ slot.label }}</span
						>
					</div>
				</div>
				<p
					:class="[
						'mt-2 text-center text-[10px] font-bold tracking-wide',
						isDark ? 'text-emerald-300' : 'text-[#0f3460]',
					]"
				>
					✓ RESERVACION CONFIRMADA
				</p>
			</div>

			<!-- Cuerpo, sólo como referencia visual -->
			<div class="space-y-1.5 bg-white px-3 py-3">
				<div class="h-2 w-2/3 rounded bg-gray-100" />
				<div class="h-2 w-1/2 rounded bg-gray-100" />
				<div class="h-10 rounded bg-gray-100" />
			</div>

		</div>

		<p class="mt-2 text-xs text-gray-400">
			Si no colocas ninguna marca, el correo sale sin logo — nunca con el de
			Enlacebooking.
		</p>
	</div>
</template>
